/**
 * 다국어(i18n) 로케일 정의 — UI 언어 전환. 표시 언어는 Settings.locale에 저장되고,
 * useT 훅이 반응형으로 읽는다. 번역 문자열은 strings.ts에 둔다.
 *
 * 범위(정직): 현재는 "항상 보이는 표면"(사이드바 내비·앱 제목·공통 라벨)을 15개 언어로
 * 늬앙스까지 번역했다. 나머지 화면 문자열은 점진적으로 키를 추가해 채운다 — 누락 키는
 * en-US → ko 순으로 폴백하므로 화면이 깨지지 않는다.
 */

export type Locale =
  | "ko"
  | "en-US"
  | "en-GB"
  | "fr"
  | "es"
  | "ru"
  | "zh"
  | "ar"
  | "de"
  | "ja"
  | "pt"
  | "pt-BR"
  | "hi"
  | "id"
  | "it";

export interface LocaleInfo {
  code: Locale;
  /** 그 언어 사용자가 읽는 자기 언어 이름(자국어 표기). */
  nativeName: string;
  /** 텍스트 방향. 아랍어만 rtl. */
  dir: "ltr" | "rtl";
}

/** 선택 가능한 로케일 목록(선택기 표시 순서). */
export const LOCALES: LocaleInfo[] = [
  { code: "ko", nativeName: "한국어", dir: "ltr" },
  { code: "en-US", nativeName: "English (US)", dir: "ltr" },
  { code: "en-GB", nativeName: "English (UK)", dir: "ltr" },
  { code: "ja", nativeName: "日本語", dir: "ltr" },
  { code: "zh", nativeName: "中文", dir: "ltr" },
  { code: "fr", nativeName: "Français", dir: "ltr" },
  { code: "de", nativeName: "Deutsch", dir: "ltr" },
  { code: "es", nativeName: "Español", dir: "ltr" },
  { code: "it", nativeName: "Italiano", dir: "ltr" },
  { code: "pt", nativeName: "Português (Portugal)", dir: "ltr" },
  { code: "pt-BR", nativeName: "Português (Brasil)", dir: "ltr" },
  { code: "ru", nativeName: "Русский", dir: "ltr" },
  { code: "ar", nativeName: "العربية", dir: "rtl" },
  { code: "hi", nativeName: "हिन्दी", dir: "ltr" },
  { code: "id", nativeName: "Bahasa Indonesia", dir: "ltr" },
];

const LOCALE_SET = new Set(LOCALES.map((l) => l.code));

/** 임의 문자열을 유효한 Locale로 정규화(미지원/미설정은 ko). */
export function normalizeLocale(value: string | undefined | null): Locale {
  if (value && LOCALE_SET.has(value as Locale)) return value as Locale;
  return "ko";
}

/** 로케일의 텍스트 방향. */
export function localeDir(locale: Locale): "ltr" | "rtl" {
  return LOCALES.find((l) => l.code === locale)?.dir ?? "ltr";
}
